import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, HelpCircle, Map, SlidersHorizontal, Scale, FileText, BookOpen, FlaskConical, Boxes, Compass } from "lucide-react";
import { PHILOSOPHY, LENSES } from "../data/content";
import WaitlistModal from "../components/WaitlistModal";
import Seo from "../components/Seo";

const GUT_CHECK = [
  "You want to reduce toxic exposure but don't know where to begin.",
  "Every article recommends something different.",
  "You don't have time to replace everything in your home.",
  "You're looking for practical guidance, not fear-based messaging.",
  "You want to know which decisions actually matter.",
];

const METHOD = [
  { icon: Map, title: "Map", desc: "Identify exposure pathways and understand how cumulative burden develops across systems." },
  { icon: SlidersHorizontal, title: "Prioritize", desc: "Evaluate relative impact instead of treating every exposure equally." },
  { icon: Scale, title: "Evaluate", desc: "Consider real-world constraints such as cost, cognitive load, practicality, and long-term sustainability." },
  { icon: FileText, title: "Translate", desc: "Convert complex evidence into Greenpapers, Digital Blueprints, Frameworks, Toolkits, and practical implementation resources." },
];

const FIND = [
  { icon: BookOpen, color: "#00E599", name: "Greenpapers", to: "/greenpapers", cta: "Explore Greenpapers",
    desc: "Research-backed analyses that explain what matters, why it matters, and how to make better environmental health decisions." },
  { icon: FlaskConical, color: "#43C6F0", name: "Sample Library", to: null, cta: "Join the Waitlist", status: "Coming Soon",
    desc: "Carefully selected lower-tox products you can evaluate before making the switch." },
  { icon: Boxes, color: "#FFB259", name: "Products", to: "/products", cta: "Explore Products",
    desc: "Digital Blueprints, Frameworks, Toolkits, and carefully curated products that help you turn knowledge into practical action." },
  { icon: Compass, color: "#B98CFF", name: "Services", to: "/services", cta: "Learn About Services",
    desc: "Engineering-based guidance for households and organizations seeking structured, practical approaches to reducing cumulative exposure and improving decision-making." },
];

const Reveal = ({ children, i = 0, className = "" }) => (
  <motion.div initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-60px" }} transition={{ duration: 0.6, delay: i * 0.08 }} className={className}>
    {children}
  </motion.div>
);

export default function AboutPage() {
  return (
    <div data-testid="about-page">
      <Seo title="About, Why My Eco Engineer Exists" path="/about"
        description="My Eco Engineer brings systems thinking to cumulative exposure. Founded by Stanley Colimon, an Industrial Engineer applying engineering principles to environmental health." />
      <section className="grid-bg grain border-b border-border">
        <div className="mx-auto max-w-7xl px-5 sm:px-8 py-24">
          <p className="mono-label">About · why we exist</p>
          <h1 className="mt-5 font-display text-4xl sm:text-5xl tracking-tight max-w-3xl leading-[1.05]">
            The problem is not a lack of information. It is a lack of <span className="text-primary">structure</span>.
          </h1>
          <p className="mt-7 max-w-2xl text-base md:text-lg text-foreground/65 leading-relaxed">
            Modern life exposes people to thousands of chemical and material inputs while requiring constant cognitive
            management of decisions, tradeoffs, and risk evaluation. My Eco Engineer was created to bring systems
            thinking to the problem of cumulative exposure, applying engineering principles to environmental health.
          </p>
        </div>
      </section>

      {/* what we do */}
      <section className="border-b border-border" data-testid="what-we-do">
        <div className="mx-auto max-w-5xl px-5 sm:px-8 py-24">
          <Reveal>
            <p className="mono-label">What we do</p>
            <h2 className="mt-4 font-display text-3xl md:text-4xl tracking-tight max-w-3xl">
              Engineering principles, applied to environmental health.
            </h2>
          </Reveal>
          <Reveal i={1}>
            <div className="mt-8 space-y-6 max-w-3xl text-base md:text-lg text-foreground/70 leading-relaxed">
              <p>
                My Eco Engineer applies engineering principles to environmental health. We create structured
                frameworks, Greenpapers, digital tools, and practical implementation resources that help people
                reduce cumulative exposure without unnecessary complexity.
              </p>
              <p>
                Rather than evaluating products in isolation, we examine how exposures interact across homes,
                products, behaviors, and everyday environments so people can make better decisions with confidence.
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* quick gut-check */}
      <section className="border-b border-border bg-surface/30" data-testid="gut-check">
        <div className="mx-auto max-w-5xl px-5 sm:px-8 py-24">
          <Reveal>
            <p className="mono-label">A quick gut-check</p>
            <h2 className="mt-4 font-display text-3xl md:text-4xl tracking-tight">Does this sound familiar?</h2>
          </Reveal>
          <div className="mt-10 space-y-px bg-border border border-border">
            {GUT_CHECK.map((g, i) => (
              <Reveal key={i} i={i}>
                <div className="flex items-start gap-4 bg-surface p-6">
                  <HelpCircle className="h-5 w-5 shrink-0 text-primary/70 mt-0.5" strokeWidth={1.5} />
                  <p className="text-base text-foreground/75 leading-relaxed">{g}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal i={GUT_CHECK.length}>
            <p className="mt-8 font-display text-xl md:text-2xl font-light leading-snug text-foreground/90 border-l-2 border-primary pl-5">
              That's exactly what <span className="text-primary">My Eco Engineer</span> was built to solve.
            </p>
          </Reveal>
        </div>
      </section>

      {/* philosophy */}
      <section className="mx-auto max-w-7xl px-5 sm:px-8 py-24">
        <Reveal className="mb-12"><p className="mono-label">Our philosophy · four principles</p></Reveal>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-border border border-border">
          {PHILOSOPHY.map((p, i) => {
            const c = LENSES[i % LENSES.length].color;
            return (
            <Reveal key={p.k} i={i}>
              <div className="relative bg-surface p-8 h-full overflow-hidden group hover:bg-surface-elevated transition-colors">
                <span className="absolute top-0 left-0 h-full w-1" style={{ background: c }} />
                <span className="font-mono text-xs" style={{ color: c }}>{`0${i + 1}`}</span>
                <h3 className="mt-4 font-display text-2xl font-medium">{p.k}</h3>
                <p className="mt-2 text-foreground/60">{p.v}</p>
              </div>
            </Reveal>
          );})}
        </div>
      </section>

      {/* framework */}
      <section className="border-y border-border bg-surface/30">
        <div className="mx-auto max-w-7xl px-5 sm:px-8 py-24">
          <Reveal className="mb-12">
            <p className="mono-label">The framework · four interconnected domains</p>
            <h2 className="mt-4 font-display text-3xl md:text-4xl tracking-tight max-w-2xl">Cumulative exposure reflects how baseline conditions, direct inputs, and internal capacity interact over time.</h2>
          </Reveal>
          <div className="space-y-px bg-border border border-border">
            {LENSES.map((l, i) => (
              <Reveal key={l.slug} i={i}>
                <Link to={`/greenpapers/category/${l.slug}`} className="group relative flex flex-col sm:flex-row sm:items-center gap-5 bg-surface hover:bg-surface-elevated p-6 transition-colors overflow-hidden">
                  <span className="absolute left-0 top-0 h-full w-[3px]" style={{ background: l.color }} />
                  <div className="relative h-20 w-20 sm:h-24 sm:w-24 shrink-0 overflow-hidden border border-border">
                    <img src={l.image} alt={l.title} loading="lazy" className="absolute inset-0 h-full w-full object-cover opacity-60 group-hover:opacity-90 group-hover:scale-105 transition-all duration-500" />
                    <div className="absolute inset-0" style={{ background: `linear-gradient(135deg, ${l.color}33, transparent)` }} />
                  </div>
                  <span className="font-mono text-2xl w-8 shrink-0" style={{ color: l.color }}>{l.index}</span>
                  <div className="flex-1">
                    <h3 className="font-display text-xl font-medium">{l.title}</h3>
                    <p className="mt-1 text-sm text-foreground/55 leading-relaxed">{l.long}</p>
                  </div>
                  <ArrowRight className="h-5 w-5 text-foreground/40 group-hover:translate-x-1 transition-all shrink-0" />
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* our method */}
      <section className="mx-auto max-w-7xl px-5 sm:px-8 py-24" data-testid="our-method">
        <Reveal className="max-w-2xl mb-14">
          <p className="mono-label">Our method</p>
          <h2 className="mt-4 font-display text-3xl md:text-4xl tracking-tight">A repeatable engineering process.</h2>
          <p className="mt-5 text-base text-foreground/65 leading-relaxed">
            Every topic published by My Eco Engineer follows the same structured engineering process. This approach
            helps separate meaningful risk from background noise while keeping recommendations practical and sustainable.
          </p>
        </Reveal>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 relative">
          {METHOD.map((m, i) => (
            <Reveal key={m.title} i={i}>
              <div className="relative h-full">
                {i < METHOD.length - 1 && (
                  <ArrowRight className="hidden lg:block absolute top-5 -right-5 h-5 w-5 text-primary/40 z-10" />
                )}
                <div className="border-t-2 border-primary/60 pt-6 h-full">
                  <div className="flex items-center justify-between">
                    <span className="font-mono text-3xl font-light text-primary">{`0${i + 1}`}</span>
                    <span className="flex h-10 w-10 items-center justify-center border border-primary/40 text-primary">
                      <m.icon className="h-5 w-5" strokeWidth={1.5} />
                    </span>
                  </div>
                  <h3 className="mt-5 font-display text-xl font-medium">{m.title}</h3>
                  <p className="mt-3 text-sm text-foreground/60 leading-relaxed">{m.desc}</p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* what you'll find here */}
      <section className="border-y border-border bg-surface/30" data-testid="what-youll-find">
        <div className="mx-auto max-w-7xl px-5 sm:px-8 py-24">
          <Reveal className="max-w-2xl mb-12">
            <p className="mono-label">What you'll find here</p>
            <h2 className="mt-4 font-display text-3xl md:text-4xl tracking-tight">One integrated ecosystem.</h2>
            <p className="mt-5 text-base text-foreground/65 leading-relaxed">
              My Eco Engineer provides multiple ways to learn, evaluate, and implement lower-tox living through an
              integrated ecosystem of educational resources and practical tools.
            </p>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-border border border-border">
            {FIND.map((f, i) => (
              <Reveal key={f.name} i={i}>
                <div data-testid={`find-${f.name.toLowerCase().replace(/\s+/g, "-")}`} className="group relative flex flex-col h-full bg-surface p-8 overflow-hidden">
                  <span className="absolute top-0 left-0 h-[3px] w-full opacity-70" style={{ background: f.color }} />
                  <div className="flex items-center justify-between">
                    <span className="flex h-11 w-11 items-center justify-center border" style={{ borderColor: `${f.color}66`, color: f.color }}>
                      <f.icon className="h-5 w-5" strokeWidth={1.5} />
                    </span>
                    {f.status && (
                      <span className="inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-widest px-2.5 py-1 border rounded-full" style={{ color: f.color, borderColor: `${f.color}66`, backgroundColor: `${f.color}12` }}>
                        <span className="h-1.5 w-1.5 rounded-full" style={{ background: f.color }} />{f.status}
                      </span>
                    )}
                  </div>
                  <h3 className="mt-6 font-display text-2xl font-medium">{f.name}</h3>
                  <p className="mt-3 text-sm text-foreground/60 leading-relaxed flex-1">{f.desc}</p>
                  {f.to ? (
                    <Link to={f.to} data-testid={`find-cta-${f.name.toLowerCase().replace(/\s+/g, "-")}`}
                      className="mt-6 inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-widest hover:gap-3 transition-all" style={{ color: f.color }}>
                      {f.cta} <ArrowRight className="h-3.5 w-3.5 shrink-0" />
                    </Link>
                  ) : (
                    <WaitlistModal slug="sample-library" name="Sample Library"
                      triggerTestid={`find-cta-${f.name.toLowerCase().replace(/\s+/g, "-")}`}
                      triggerClassName="mt-6 inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-widest cursor-pointer hover:gap-3 transition-all">
                      <span style={{ color: f.color }} className="inline-flex items-center gap-2">{f.cta} <ArrowRight className="h-3.5 w-3.5 shrink-0" /></span>
                    </WaitlistModal>
                  )}
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* founder */}
      <section className="mx-auto max-w-7xl px-5 sm:px-8 py-24 grid lg:grid-cols-12 gap-12">
        <Reveal className="lg:col-span-4">
          <div className="relative aspect-[4/5] border border-border bg-base grid-bg flex items-center justify-center">
            <span className="font-display text-7xl font-medium text-primary">SC</span>
            <span className="absolute bottom-4 left-4 mono-label">Stanley Colimon</span>
          </div>
        </Reveal>
        <Reveal i={1} className="lg:col-span-8">
          <p className="mono-label">About the founder</p>
          <h2 className="mt-4 font-display text-3xl tracking-tight">Stanley Colimon</h2>
          <p className="mt-2 font-mono text-xs uppercase tracking-widest text-foreground/50">Industrial Engineer · Certified Project Manager</p>
          <div className="mt-6 space-y-5 text-base text-foreground/70 leading-relaxed max-w-2xl">
            <p>Stanley Colimon is the founder of My Eco Engineer, with a background in structured problem-solving, process optimization, and systems design. Before creating My Eco Engineer, he applied engineering methodologies to complex operational challenges, focusing on prioritization, risk evaluation, and decision-making in high-demand environments.</p>
            <p>He approaches environmental health as a complex system that can be mapped, prioritized, and improved using structured engineering methodologies. That same systems-based thinking underlies every Greenpaper, Digital Blueprint, Framework, Tool, and service produced by My Eco Engineer.</p>
          </div>
        </Reveal>
      </section>
    </div>
  );
}
